<script setup lang="ts">
  import { computed, onMounted, provide, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { onBeforeRouteLeave } from 'vue-router';
  import { SearchHeader, SearchFilterModal } from '@/features/Search';
  import { SearchPageBody } from '@/widgets/document-actions';
  import { SearchToolbar, SelectInput, TabsMenu } from '@/shared/ui';
  import { useFilterStore } from '@/entities/Filter';
  import { useTableFilterModal } from '@/shared/lib/use';
  import { ORDER_OPTIONS, QUANTITY_OPTIONS } from '@/shared/config';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  import {
    useDocumentSearchTypeStore,
    formatDateForApi,
    DocumentType,
    SEARCH_TYPE_FIELD_OPTIONS,
    documentTypeToTabOptionId,
    tabOptionIdToDocumentType,
    isDocumentRegistrationTabOptionId,
    type DocumentRegistrationTabOptionId,
  } from '@/shared/lib';
  import { useDocumentSelection } from '@/features/Document';

  import type { SearchSort } from '@/shared/lib';
  import type { FilterOptions } from '@/entities/Document';

  const route = useRoute();
  const router = useRouter();
  const searchTerm = ref('');
  const searchSort = ref<SearchSort>({ sort: '', order: undefined });
  const filterValue = ref<FilterOptions>({});
  const filterStore = useFilterStore();
  const { searchType, setSearchType } = useDocumentSearchTypeStore();
  const { resetSelection } = useDocumentSelection();

  const searchFields = SEARCH_TYPE_FIELD_OPTIONS;

  const TAB_OPTIONS = [
    { id: 'documentos', text: 'Documentos' },
    { id: 'registros', text: 'Registros' },
  ] satisfies Array<{ id: DocumentRegistrationTabOptionId; text: string }>;

  const urlDocumentType = route.query.type as string;
  const selectedDocumentType = ref<DocumentType>(
    urlDocumentType === DocumentType.REGISTRATION ? DocumentType.REGISTRATION : DocumentType.DOCUMENT
  );
  const selectedTabId = computed(() => documentTypeToTabOptionId(selectedDocumentType.value));

  const effectiveFilter = computed<FilterOptions>(() => ({
    ...filterValue.value,
    type: selectedDocumentType.value,
  }));

  const handleTabSelected = (optionId: string) => {
    if (!isDocumentRegistrationTabOptionId(optionId)) return;

    const nextDocumentType = tabOptionIdToDocumentType(optionId);
    if (nextDocumentType === selectedDocumentType.value) return;

    selectedDocumentType.value = nextDocumentType;
    searchTerm.value = '';
    searchSort.value = { sort: '', order: undefined };
    filterValue.value = {};
    filterStore.clearFilters();
  };

  const urlSearchType = route.query.searchType as string;
  if (urlSearchType === 'name' || urlSearchType === 'prefix') {
    setSearchType(urlSearchType);
  }

  watch(searchType, (newType, oldType) => {
    if (oldType !== undefined && newType !== oldType) {
      searchTerm.value = '';
    }
  });

  const handleSearch = (queryString: string) => {
    searchTerm.value = queryString;
  };

  const handleSort = (sort: string, order: string) => {
    searchSort.value = { sort, order: order as 'asc' | 'desc' | undefined };
  };

  const handleFilter = (filter: FilterOptions) => {
    filterValue.value = filter;
    if (Object.keys(filter).length === 0) {
      filterStore.clearFilters();
    } else {
      filterStore.setFilters(filter);
    }
  };

  const { isFilterActive, openFilterModal } = useTableFilterModal(
    SearchFilterModal,
    filterStore,
    handleFilter,
    () => filterValue.value,
    () => ({ documentType: selectedDocumentType.value })
  );

  const handleFilterClicked = () => {
    openFilterModal();
  };

  provide(SortOrderInjectionKey, {
    sort: computed(() => searchSort.value.sort ?? ''),
    order: computed(() => searchSort.value.order ?? ''),
  });

  function buildQueryFromState() {
    const query: Record<string, string> = {
      type: selectedDocumentType.value,
    };
    if (searchTerm.value) {
      query.term = searchTerm.value;
    }
    if (searchType.value) {
      query.searchType = searchType.value;
    }
    if (searchSort.value.sort && searchSort.value.order) {
      query.sort = searchSort.value.sort;
      query.order = searchSort.value.order;
    }
    Object.entries(filterValue.value).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (typeof value === 'number' && value === 0) return;
        query[key] =
          value instanceof Date ? (formatDateForApi(value) ?? '') : String(value);
      }
    });
    return query;
  }

  const hasHydratedFromUrl = ref(false);

  watch(
    [searchTerm, searchSort, filterValue, searchType, selectedDocumentType],
    () => {
      if (!hasHydratedFromUrl.value) return;
      router.replace({ path: route.path, query: buildQueryFromState() });
    },
    { deep: true }
  );

  onMounted(() => {
    searchTerm.value = (route.query.term as string) || '';
    searchSort.value = {
      sort: (route.query.sort as string) || '',
      order: (route.query.order as 'asc' | 'desc' | undefined) || undefined,
    };

    const urlFilter: Partial<FilterOptions> = {};
    Object.keys(route.query).forEach((key) => {
      if (!['term', 'sort', 'order', 'searchType', 'type'].includes(key)) {
        const value = route.query[key];
        if (value !== undefined && value !== null) {
          (urlFilter as Record<string, unknown>)[key] = value;
        }
      }
    });
    filterValue.value = urlFilter as FilterOptions;
    hasHydratedFromUrl.value = true;
  });

  onBeforeRouteLeave(() => {
    resetSelection();
    filterStore.clearFilters();
  });
</script>

<template>
  <div class="max-w-full h-max p-6">
    <h3 class="mt-4 flex font-semibold text-xl text-gray-07">
      Pesquisa
    </h3>
    <SearchPageBody
      :search-term="searchTerm"
      :search-sort="searchSort"
      :filter="effectiveFilter"
      :document-type="selectedDocumentType"
      :order-options="ORDER_OPTIONS"
      :is-filter-active="isFilterActive"
      search-url="/document-revisions/search"
      context="search"
      @update:sort="handleSort"
      @filter-clicked="handleFilterClicked"
    >
      <template #header="{ showQuantitySelect, selectedQuantity, changeQuantity }">
        <div class="mt-4 mb-6 flex flex-col gap-2 border border-gray-02 rounded-2xl p-4">
          <TabsMenu
            class="!rounded-lg !p-0.5"
            :options="TAB_OPTIONS"
            :selected-option="selectedTabId"
            visual-variant="neutral"
            muted-inactive-text
            @option-selected="handleTabSelected"
          />
          <SearchToolbar>
            <template #search>
              <SearchHeader
                :search-fields="searchFields"
                :initial-search-term="searchTerm"
                @search="handleSearch"
              />
            </template>
            <template
              v-if="showQuantitySelect"
              #quantity
            >
              <SelectInput
                :model-value="selectedQuantity"
                :options="QUANTITY_OPTIONS"
                class="cursor-pointer"
                @update:model-value="changeQuantity"
              />
            </template>
          </SearchToolbar>
        </div>
      </template>
    </SearchPageBody>
  </div>
</template>
